import { useCallback, useEffect, useRef } from 'react';

/**
 * FERMETURE « AU CLIC DEHORS » — source UNIQUE de la règle du GESTE ENTIER (XFILE-79).
 *
 * Règle : on ne ferme que si le geste a commencé ET s'est terminé dehors (sur le voile pour une
 * modale, hors du panneau pour un menu). Un geste à cheval — appui d'un côté, relâchement de
 * l'autre — ne ferme JAMAIS.
 *
 * ── Le mécanisme, à comprendre avant de « simplifier » ces gardes ───────────────────────────
 *
 * Un `click` se déclenche sur l'ANCÊTRE COMMUN du `pointerdown` et du `pointerup`. Sélectionner
 * du texte dans un champ de la modale en débordant sur le voile produit donc un `click` dont la
 * cible EST le voile. D'où deux fausses corrections, l'une et l'autre déjà tentées ailleurs :
 *
 *  - tester seulement `e.target === e.currentTarget` au `click` : dans ce cas la cible EST déjà
 *    le voile, le test passe, et la modale ferme sur une simple sélection de texte ;
 *  - poser un `stopPropagation` sur le panneau : il n'est même pas sur le chemin de propagation
 *    de ce `click`-là, puisque la cible est le voile — le gestionnaire n'est jamais atteint.
 *
 * Fermer au `pointerdown` (ce que faisaient les SIX surfaces de ce dépôt avant XFILE-79) ne
 * corrige pas le défaut : ça le DÉPLACE. L'appui parti du champ ne ferme plus, mais un appui
 * commencé sur le voile et relâché dans le panneau ferme — et la modale disparaît sous le doigt
 * au moment précis où l'utilisateur revient dedans. Ça vaut aussi au TACTILE.
 *
 * ── Le réarmement ───────────────────────────────────────────────────────────────────────────
 *
 * Les drapeaux sont RÉAFFECTÉS à chaque `pointerdown`/`pointerup`, jamais accumulés par `||` :
 * un drapeau resté armé d'un geste précédent fermerait au clic suivant, c'est-à-dire qu'on
 * remplacerait un défaut par un autre.
 */

/** Props à étaler sur l'élément VOILE (backdrop) d'une modale. */
export interface OverlayDismissProps {
  onPointerDown: React.PointerEventHandler<HTMLElement>;
  onPointerUp: React.PointerEventHandler<HTMLElement>;
  onClick: React.MouseEventHandler<HTMLElement>;
}

/**
 * Voile de MODALE : ne ferme que si l'appui ET le relâchement ont eu lieu sur le voile lui-même
 * (et non sur un descendant, c'est-à-dire le panneau).
 *
 * On ferme au `click` — et non au `pointerup` — pour rester sur la notion de clic de la
 * plateforme. Le `click` est de toute façon émis après les deux, donc les deux drapeaux sont
 * déjà connus quand on tranche.
 */
export function useOverlayDismiss(onDismiss: () => void): OverlayDismissProps {
  const startedOnOverlay = useRef(false);
  const endedOnOverlay = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    startedOnOverlay.current = e.target === e.currentTarget;
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    endedOnOverlay.current = e.target === e.currentTarget;
  }, []);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement>): void => {
      const wholeGestureOnOverlay =
        startedOnOverlay.current && endedOnOverlay.current && e.target === e.currentTarget;
      // Désarmement AVANT de fermer : ce clic est consommé, quel qu'en soit le verdict.
      startedOnOverlay.current = false;
      endedOnOverlay.current = false;
      if (wholeGestureOnOverlay) onDismiss();
    },
    [onDismiss],
  );

  return { onPointerDown, onPointerUp, onClick };
}

/**
 * MENUS / POPOVERS / TIROIRS : ferme quand un geste ENTIER (appui + relâchement) a eu lieu hors
 * du panneau désigné par `rootRef`.
 *
 * ⚠ On tranche au `pointerup`, PAS au `click`, et c'est délibéré : ces écouteurs sont posés sur
 * `document` par un effet déclenché par l'ouverture, laquelle a lieu dans le `onClick` du
 * déclencheur. Un écouteur `click` recevrait donc le clic d'ouverture LUI-MÊME et refermerait le
 * menu dans la foulée — le menu ne s'ouvrirait jamais. Le `pointerup` de ce geste-là, lui, est
 * déjà passé quand l'écouteur est posé.
 *
 * `onDismiss` doit être stable (`useCallback`) : l'effet en dépend.
 */
export function useOutsideGestureDismiss(
  open: boolean,
  rootRef: React.RefObject<HTMLElement | null>,
  onDismiss: () => void,
): void {
  const startedOutside = useRef(false);

  useEffect(() => {
    if (!open) return;

    const isOutside = (target: EventTarget | null): boolean => {
      const root = rootRef.current;
      // Racine absente ⇒ on ne ferme pas (fail-closed : mieux vaut un menu qui reste ouvert
      // qu'un menu qui se ferme sur un geste qu'on n'a pas su localiser).
      return root !== null && target instanceof Node && !root.contains(target);
    };

    const onPointerDown = (e: PointerEvent): void => {
      startedOutside.current = isOutside(e.target);
    };
    const onPointerUp = (e: PointerEvent): void => {
      const wholeGestureOutside = startedOutside.current && isOutside(e.target);
      startedOutside.current = false;
      if (wholeGestureOutside) onDismiss();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [open, rootRef, onDismiss]);
}
